import { Component, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import {
  DatePickerFormValidators,
  DatepickerHelper
} from '../../../../shared/validators/date-picker-form-validators';
import { setControlDisabled } from '../../../../shared/utils/set-control-disabled';
import { Document } from '@models/documents/document';
import { EntityChangeData } from '@models/documents/entity-change-data';
import {
  CompanyChangeHoldingCompanyActionEnum
} from './CompanyChangeHoldingCompanyActionEnum';
import { SelectOption } from '@models/ui-models/select-options';
import { allChangeDescriptionOptions } from './all-change-description-options.constant';
import { ChangeHoldingCompanyStepsEnum } from './change-holding-company-steps.enum';
import { CompanyChangeHoldingCompany } from '../../../models/company';
import {
  CompanyNameAcnFormGroupComponent
} from "../../../reusable-form-groups/company-name-acn-form-group/company-name-acn-form-group.component";
import { SelectComponent } from "@shared/controls/select/select.component";
import { InputComponent } from "@shared/controls/input/input.component";
import { DatePickerComponent } from "@shared/controls/date-picker/date-picker.component";
import {
  FlagsAutocompleteComponent
} from "@shared/controls/flags-autocomplete/flags-autocomplete.component";
import { CountriesService } from "../../../../shared/services/countries.service";
import { BaseStepperFormComponent, StepperFormComponent, StepperFormDescriptionComponent } from "../../stepper-form";
import { Company } from "@models/companies/company";
import { DisclaimerComponent } from "@shared/components/commons/disclaimer/disclaimer.component";
import { getForm484LateFeeNotification } from "../../../utils/get-late-fee-notification";

@Component({
  selector: 'app-a3-holding-company-change',
  standalone: true,
  templateUrl: './company-change-holding-company.component.html',
  styleUrl: '../../stepper-form/base-stepper-component/base-stepper-form.component.scss',
    imports: [
        ReactiveFormsModule,
        InputComponent,
        SelectComponent,
        DatePickerComponent,
        StepperFormComponent,
        StepperFormDescriptionComponent,
        CompanyNameAcnFormGroupComponent,
        FlagsAutocompleteComponent,
        DisclaimerComponent
    ]
})
export class CompanyChangeHoldingCompanyComponent extends BaseStepperFormComponent<ChangeHoldingCompanyStepsEnum, CompanyChangeHoldingCompany> implements OnInit {
  override readonly StepsEnum = ChangeHoldingCompanyStepsEnum;

  changeDescriptionOptions: SelectOption[] = [];
  minDate!: NgbDateStruct;
  maxDate!: NgbDateStruct;
  errorMessage = '';

  form = new FormGroup({
    ...CompanyNameAcnFormGroupComponent.defineFormControls(),
    aboardCompanyName: new FormControl<string | null>(null, [Validators.required, Validators.minLength(3)]),
    action: new FormControl<CompanyChangeHoldingCompanyActionEnum | string | null>(null, Validators.required),
    holdingCompanyPlaceOfIncorporation: new FormControl<string | null>(null, Validators.required),
    changeDate: new FormControl<Date | null>(null, [Validators.required, DatePickerFormValidators.dateFormatValidator]),
  });

  override stepperForm = new FormGroup({
    [ChangeHoldingCompanyStepsEnum.FormDescription]: new FormGroup({}),
    [ChangeHoldingCompanyStepsEnum.ChangeHoldingCompany]: this.form
  });

  isAustraliaSelected = signal(false);

  constructor() {
    super();
    this.setupSteps(ChangeHoldingCompanyStepsEnum);
  }

  ngOnInit(): void {
    this.minDate = DatepickerHelper.getDateOfEstablishmentMinDate(this.companyChangeData.dateOfEstablishment);
    this.maxDate = DatepickerHelper.getNextNYearsStruct(10);
    this.setDefaultFormState();

    this.listenFormChanges();
    this.listenHoldingCompanyPlaceOfIncorporation();
    this.listenChangeDescription();
    this.listenChangeDateChange(this.form.controls.changeDate, getForm484LateFeeNotification);

    this.setupChange();
  }

  override afterSubmit(changes: EntityChangeData[]) {
    this.setupChange(changes[0] as CompanyChangeHoldingCompany);
  }

  override setupChange(change: CompanyChangeHoldingCompany = this.formModel) {
    this.form.patchValue({
      aboardCompanyName: change.holdingCompanyName,
      name: change.holdingCompanyName,
      acn: change.holdingCompanyAcn,
      holdingCompanyPlaceOfIncorporation: change.holdingCompanyPlaceOfIncorporation,
    });

    this.form.controls.aboardCompanyName.setValue(change.holdingCompanyName ?? null);
    this.form.controls.holdingCompanyPlaceOfIncorporation.setValue(change.holdingCompanyPlaceOfIncorporation ?? null);

    if (!this.isEdit && change === this.formModel)
      return;

    this.setupActionOptions();
    this.updateControlsWithChangeAction(change.action);

    this.form.patchValue({
      ...change,
      name: change.holdingCompanyName,
      acn: change.holdingCompanyAcn,
      aboardCompanyName: change.holdingCompanyName,
      changeDate: DatepickerHelper.toDate(change.changeDate)
    });
  }

  override buildDocument(): Document | null {
    const formValue = this.form.value;
    const ultimateHoldingCompanyChanges = new CompanyChangeHoldingCompany({
      ...formValue as Partial<CompanyChangeHoldingCompany>,
      changeDate: formValue.changeDate!,
      holdingCompanyName: formValue.name ?? formValue.aboardCompanyName ?? '',
      holdingCompanyAcn: formValue.acn!,
    });

    try {
      return new Document({
        changes: [ultimateHoldingCompanyChanges],
        entityId: this.companyChangeData.entityId,
        type: 'c:484',
        documentId: this.companyChangeData?.documentId,
      });
    } catch (error) {
      console.warn(error);
      this.toastr.error('Failed to create Document.', 'Error');
      return null;
    }
  }

  onCompanySelected(company: Company): void {
    this.form.patchValue({
      name: company?.name,
      acn: company?.acn,
    });
  }

  private setDefaultFormState(): void {
    this.form.controls.changeDate.setValue(DatepickerHelper.getToday());

    this.setupActionOptions();
  }

  private setupActionOptions(): void {
    const setOtherOptions = () => {
      this.changeDescriptionOptions = allChangeDescriptionOptions
        .filter(option => option.value !== CompanyChangeHoldingCompanyActionEnum.AP);
    };

    const setAppointOptions = () => {
      this.changeDescriptionOptions = allChangeDescriptionOptions
        .filter(option => option.value === CompanyChangeHoldingCompanyActionEnum.AP);
    };

    if (this.isEdit) {
      if (this.formModel.action === CompanyChangeHoldingCompanyActionEnum.AP) {
        setAppointOptions();
      } else {
        setOtherOptions();
      }
    } else {
      if (this.formModel.holdingCompanyName) {
        setOtherOptions();
      } else {
        setAppointOptions();
      }
    }
  }

  private listenFormChanges(): void {
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.errorMessage = '';
      });
  }

  private listenHoldingCompanyPlaceOfIncorporation(): void {
    this.form.controls.holdingCompanyPlaceOfIncorporation.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((country) => {
        const isAustraliaSelected = CountriesService.isAustralianStateOrAustralia(country);

        if (isAustraliaSelected) {
          this.form.controls.aboardCompanyName?.setValue(this.form.controls.name?.getRawValue() ?? null);
        } else {
          this.form.controls.name?.setValue(this.form.controls.aboardCompanyName?.getRawValue() ?? null);
        }

        setControlDisabled(this.form.controls.acn, !isAustraliaSelected);
        setControlDisabled(this.form.controls.name, !isAustraliaSelected);
        setControlDisabled(this.form.controls.aboardCompanyName, isAustraliaSelected);
        this.isAustraliaSelected.set(isAustraliaSelected);
      });
  }

  private listenChangeDescription(): void {
    this.form.controls.action.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.updateControlsWithChangeAction(value as CompanyChangeHoldingCompanyActionEnum);
      });
  }

  private updateControlsWithChangeAction(action: CompanyChangeHoldingCompanyActionEnum | null): void {
    switch (action) {
      case CompanyChangeHoldingCompanyActionEnum.AP:
        // Appoint UHC - use default settings of form
        break;

      case CompanyChangeHoldingCompanyActionEnum.NO:
        // Cease UHC - disable everything, except date of change and change type
        setControlDisabled(this.form.controls.aboardCompanyName);
        setControlDisabled(this.form.controls.holdingCompanyPlaceOfIncorporation);
        this.form.updateValueAndValidity();

        break;

      default:
        // Change UHC name
        this.form.controls.holdingCompanyPlaceOfIncorporation?.enable();
        this.form.controls.aboardCompanyName?.enable();
    }
  }

  get isOverseasCompany(): boolean {
    return CountriesService.isAustralianStateOrAustralia(this.form.controls.holdingCompanyPlaceOfIncorporation.value);
  }

  get hideDataPart(): boolean {
    return this.form.controls.action.getRawValue() === null || this.form.controls.action.getRawValue() === CompanyChangeHoldingCompanyActionEnum.NO;
  }

  get hideAustralianCompanyNameAcn(): boolean {
    return this.form.controls.action.getRawValue() === null
      || !(this.form.controls.action.getRawValue() !== CompanyChangeHoldingCompanyActionEnum.NO
        && this.isAustraliaSelected());
  }

  get hideAboardCompanyName(): boolean {
    return this.form.controls.action.getRawValue() === null
      || !(this.form.controls.action.getRawValue() !== CompanyChangeHoldingCompanyActionEnum.NO
        && !this.isAustraliaSelected());
  }
}
